<template>
  <div class="wg-card" id="wg-mapping">
    <div class="wg-card-head wg-mapping-head" @click="expanded = !expanded">
      <div class="wg-sec-title">AI Page Mapping</div>
      <span :class="badge.cls">{{ badge.label }}</span>
      <button
        v-if="expanded && canRerun"
        class="wg-btn-out rerun"
        :disabled="starting"
        @click.stop="mapPages"
      >
        <i class="material-icons">refresh</i> {{ starting ? 'Starting…' : 'Re-run mapping' }}
      </button>
      <i class="material-icons wg-mapping-caret" :class="{ open: expanded }">expand_more</i>
    </div>
    <div v-if="!expanded" class="wg-map-collapsed">
      We detect new pages in Search Console and match them to your
      keywords.{{ suggestions.length > 0
        ? ` ${suggestions.length} ${suggestions.length === 1 ? 'suggestion is' : 'suggestions are'} waiting for review.`
        : '' }}
    </div>
    <template v-if="expanded">
    <div class="wg-sec-sub">
      {{ suggestions.length > 0
        ? 'We detected new pages in Search Console and matched them with your keywords. Approving a page starts tracking it immediately.'
        : 'We detect new pages in Search Console and match them with your keywords. Approving a page starts tracking it immediately.' }}
    </div>

    <!-- mapping in progress -->
    <div v-if="isRunning" class="wg-mapprog">
      <div class="mhead">
        <span class="ml">{{ run.stageLabel }}</span>
        <span class="mp">{{ run.progressPct }}%</span>
      </div>
      <div class="mbar"><i :style="{ width: run.progressPct + '%' }"></i></div>
      <div class="mnote">
        This usually takes under a minute. You can keep using POP. We'll finish in the background.
      </div>
    </div>

    <!-- run failed -->
    <div v-else-if="failed" class="wg-cempty">
      <i class="material-icons-outlined">error_outline</i>
      <div class="t">Mapping didn't finish</div>
      <div class="s">{{ run.error || 'Something went wrong while matching your pages.' }}</div>
      <button class="wg-btn-out" @click="mapPages">
        <i class="material-icons">refresh</i> Try again
      </button>
    </div>

    <!-- suggestions pending -->
    <template v-else-if="suggestions.length > 0">
      <div v-for="s in suggestions" :key="s.id" class="wg-suggest">
        <div class="left">
          <div class="kw">
            Keyword: <b>{{ s.keyword }}</b>
            <span v-if="s.sourceTag" class="wg-tag" :class="tagClass(s.sourceTag)">{{ tagLabel(s.sourceTag) }}</span>
          </div>
          <a class="surl" :href="s.url" target="_blank" rel="noopener noreferrer">{{ s.url }}</a>
          <div class="shelp">{{ s.confidenceReason }}</div>
          <div v-if="s.firstSeen" class="sseen">
            First seen by Google {{ fmtSeen(s.firstSeen) }} · used as publish date
          </div>
        </div>
        <div class="right">
          <div class="wg-conf right-align">
            <span class="lbl big" :class="s.confidence">{{ confidenceLabel(s.confidence) }}</span>
          </div>
          <div class="sacts">
            <button class="wg-sbtn decline" :disabled="resolvingId === s.id" @click="decline(s)">Decline</button>
            <button class="wg-sbtn approve" :disabled="resolvingId === s.id" @click="approve(s)">Approve</button>
          </div>
        </div>
      </div>
    </template>

    <!-- connected but never mapped -->
    <div v-else-if="!run" class="wg-cempty">
      <i class="material-icons">auto_awesome</i>
      <div class="t">No pages mapped yet</div>
      <div class="s">
        Search Console is connected. Run a mapping pass and our AI will match the pages Google
        knows about to your target keywords, then show you each match to approve.
      </div>
      <button class="wg-pill solid" :disabled="starting" @click="mapPages">
        <i class="material-icons">auto_awesome</i> {{ starting ? 'Starting…' : 'Map pages' }}
      </button>
    </div>

    <!-- all resolved / nothing found -->
    <div v-else class="wg-cempty">
      <i class="material-icons">{{ resolvedCount ? 'check' : 'search' }}</i>
      <div class="t">{{ resolvedCount ? 'Nothing left to review' : 'No new pages detected yet' }}</div>
      <div class="s">
        {{ resolvedCount
          ? `You reviewed ${resolvedCount} ${resolvedCount === 1 ? 'suggestion' : 'suggestions'}. We'll flag new pages here as soon as Search Console reports them. Nothing for you to do.`
          : "Search Console hasn't reported any new pages for this project. Once you publish something and Google indexes it, matched suggestions appear here automatically." }}
      </div>
      <button class="wg-btn-out" :disabled="starting" @click="mapPages">
        <i class="material-icons">refresh</i> {{ starting ? 'Starting…' : 'Re-run mapping' }}
      </button>
    </div>
    </template>
  </div>
</template>

<script>
const POLL_INTERVAL_MS = 1500
const RUNNING_STATUSES = ['queued', 'fetching_pages', 'matching', 'scoring']
const SOURCE_LABELS = {
  content_plan: { label: 'Content Plan', cls: 'c-green' },
  content_only: { label: 'Content only', cls: 'c-blue' },
}

export default {
  name: 'PageMappingCard',
  inject: ['wgToast'],
  props: {
    site: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      expanded: false,
      run: null,
      suggestions: [],
      starting: false,
      resolvingId: null,
      resolvedCount: 0,
      pollTimer: null,
    }
  },
  computed: {
    isRunning () {
      return Boolean(this.run) && RUNNING_STATUSES.includes(this.run.status)
    },
    failed () {
      return Boolean(this.run) && this.run.status === 'failed'
    },
    /** A pass already produced results: newly delivered keywords need a rerun
     * to show up (empty states have their own buttons). */
    canRerun () {
      return !this.isRunning && !this.failed &&
        (this.suggestions.length > 0 || Boolean(this.run && this.resolvedCount))
    },
    badge () {
      if (this.isRunning) return { label: 'Mapping…', cls: 'wg-badge-blue' }
      if (this.suggestions.length > 0) {
        return {
          label: `${this.suggestions.length} ${this.suggestions.length === 1 ? 'suggestion' : 'suggestions'}`,
          cls: 'wg-badge-blue',
        }
      }
      if (!this.run) return { label: 'Not mapped yet', cls: 'wg-badge-grey' }
      if (this.failed) return { label: 'Failed', cls: 'wg-badge-grey' }
      return this.resolvedCount
        ? { label: 'All caught up', cls: 'wg-badge-green' }
        : { label: 'Waiting on data', cls: 'wg-badge-grey' }
    },
  },
  watch: {
    'site.id': {
      handler () {
        this.resolvedCount = 0
        this.load()
      },
      immediate: true,
    },
  },
  beforeDestroy () {
    clearTimeout(this.pollTimer)
  },
  methods: {
    tagLabel (source) {
      return (SOURCE_LABELS[source] || { label: source }).label
    },
    tagClass (source) {
      return (SOURCE_LABELS[source] || { cls: 'c-grey' }).cls
    },
    fmtSeen (value) {
      const [y, m, d] = value.split('-')
      return new Date(Number(y), Number(m) - 1, Number(d))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    confidenceLabel (confidence) {
      return {
        high: 'High confidence',
        medium: 'Medium confidence',
        low: 'Low confidence',
      }[confidence] || confidence
    },
    load () {
      if (!this.site || !this.site.id) return
      this.$store.dispatch('getWinsMappingRun', this.site.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.run = response.data.run
            if (this.isRunning) this.schedulePoll()
          }
        })
        .catch(() => {})
      this.loadSuggestions()
    },
    loadSuggestions () {
      this.$store.dispatch('listWinsMappingSuggestions', this.site.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applySuggestions(response.data.suggestions || [])
          }
        })
        .catch(() => {})
    },
    applySuggestions (suggestions) {
      this.suggestions = suggestions
      this.$emit('counts-changed', { pendingSuggestionsCount: suggestions.length })
    },
    schedulePoll () {
      clearTimeout(this.pollTimer)
      this.pollTimer = setTimeout(() => {
        this.$store.dispatch('getWinsMappingRun', this.site.id)
          .then((response) => {
            if (response.data && response.data.status === 'SUCCESS') {
              const wasRunning = this.isRunning
              this.run = response.data.run
              if (this.isRunning) {
                this.schedulePoll()
              } else if (wasRunning && this.run && this.run.status === 'completed') {
                this.loadSuggestions()
                const n = this.run.suggestionsCount || 0
                this.wgToast(n
                  ? `${n} ${n === 1 ? 'page' : 'pages'} mapped, ready to review`
                  : 'Mapping finished, no new pages found')
              }
            } else if (this.isRunning) {
              this.schedulePoll()
            }
          })
          .catch(() => {
            if (this.isRunning) this.schedulePoll()
          })
      }, POLL_INTERVAL_MS)
    },
    expand () {
      this.expanded = true
    },
    mapPages () {
      this.expanded = true
      if (this.starting) return
      this.starting = true
      this.$store.dispatch('startWinsMappingRun', this.site.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.run = response.data.run
            this.schedulePoll()
          } else {
            const msg = (response.data && response.data.msg) || 'Could not start mapping'
            this.wgToast(msg, null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not start mapping', null, 'error')
        })
        .finally(() => {
          this.starting = false
        })
    },
    approve (suggestion) {
      this.resolvingId = suggestion.id
      this.$store.dispatch('approveWinsMappingSuggestion', suggestion.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.resolvedCount += 1
            this.applySuggestions(response.data.suggestions || [])
            this.wgToast('Page approved, now tracking in Published URLs')
            this.$emit('approved')
          } else {
            this.wgToast('Could not approve the suggestion', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not approve the suggestion', null, 'error')
        })
        .finally(() => {
          this.resolvingId = null
        })
    },
    decline (suggestion) {
      this.resolvingId = suggestion.id
      this.$store.dispatch('declineWinsMappingSuggestion', suggestion.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.resolvedCount += 1
            this.applySuggestions(response.data.suggestions || [])
            this.wgToast('Suggestion declined', { label: 'Undo', onClick: () => this.undoDecline(suggestion) })
          } else {
            this.wgToast('Could not decline the suggestion', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not decline the suggestion', null, 'error')
        })
        .finally(() => {
          this.resolvingId = null
        })
    },
    undoDecline (suggestion) {
      this.$store.dispatch('undoDeclineWinsMappingSuggestion', suggestion.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.resolvedCount = Math.max(0, this.resolvedCount - 1)
            this.applySuggestions(response.data.suggestions || [])
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

.wg-map-collapsed {
  font-size: 0.85rem;
  color: $pop-ink-muted;
}

.sseen {
  font-size: 0.75rem;
  color: $pop-ink-muted;
  margin-top: 6px;
}

.wg-mapping-head {
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
}

.wg-mapping-caret {
  margin-left: auto;
  color: $pop-ink-muted;
  transition: transform 0.15s;

  &.open {
    transform: rotate(180deg);
  }
}

.wg-mapping-head .rerun + .wg-mapping-caret {
  margin-left: 0;
}

.wg-card-head .rerun {
  margin-left: auto;

  .material-icons {
    font-size: 1rem;
  }
}

/* AI mapping progress */
.wg-mapprog {
  border: 1px solid $wg-panel-line;
  border-radius: 10px;
  background: $wg-lavender;
  padding: 20px 22px;

  .mhead {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 10px;
  }

  .ml {
    font-size: 0.9rem;
    font-weight: 700;
    color: $pop-teal;
  }

  .mp {
    font-size: 0.9rem;
    font-weight: 800;
    color: $pop-accent;
  }

  .mbar {
    height: 8px;
    border-radius: 4px;
    background: $pop-chart-track;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 4px;
      background: $pop-accent;
      transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  .mnote {
    font-size: 0.8rem;
    color: $pop-ink-muted;
    margin-top: 10px;
    line-height: 1.45;
  }
}

/* suggestion rows */
.wg-suggest {
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 12px;
  display: flex;
  gap: 20px;
  align-items: center;

  .left {
    flex: 1;
    min-width: 0;
  }

  .kw {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 0.85rem;
    color: $pop-teal;
    margin-bottom: 5px;
    white-space: nowrap;

    b {
      font-weight: 700;
    }
  }

  .surl {
    font-size: 0.85rem;
    color: $pop-link;
    text-decoration: none;
    display: block;
    margin-bottom: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  .shelp {
    font-size: 0.8rem;
    color: $pop-ink-faint;
    font-style: italic;
  }

  .right {
    width: 210px;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sacts {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
}

.wg-conf {
  display: flex;
  flex-direction: column;
  gap: 5px;

  &.right-align {
    align-items: flex-end;
    text-align: right;
  }

  .lbl {
    font-size: 0.7rem;
    font-weight: 700;

    &.big {
      font-size: 13px;
      font-weight: 800;
    }

    &.high {
      color: $pop-success;
    }

    &.medium {
      color: $pop-accent;
    }

    &.low {
      color: $pop-danger;
    }
  }
}
</style>
